import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { BiHeaderStats } from "@/resources/brand-intelligence/brand-intelligence.api";
import { fmtUsd, plural } from "./headerFormat";
import { CategoryNamesFitBudget } from "./CategoryNamesFit";
import { InfoTooltip } from "./InfoTooltip";
import { BI_TOOLTIPS } from "./tooltips";

interface Props {
  header: BiHeaderStats;
  refreshedAt: string | null;
  /** The primary category or niche slot: the meta row's first item. */
  badge: ReactNode;
  /** Already worded by the page: "N categories", or "N other categories" beside the badge. */
  categoryCount: string;
  /** "other" beside a primary category or niche (the count excludes it); "all" otherwise. */
  categoryCountScope: "other" | "all";
}

// Used only until the badge reports its own floor (first name in full + "+N" + chrome).
const FALLBACK_BADGE_FLOOR = 96;

export function BrandIntelligenceHeaderMeta({ header, refreshedAt, badge, categoryCount, categoryCountScope }: Props) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [budget, setBudget] = useState<number | null>(null);
  const [floor, setFloor] = useState<number | null>(null);
  const reportFloor = useCallback((width: number) => setFloor(width), []);
  const context = useMemo(() => ({ budget, onMinimum: reportFloor }), [budget, reportFloor]);

  // The badge takes the width left on the line once the other items have their natural width, so the
  // whole row stays on one line whenever it can. If that is less than the badge's floor, the badge
  // gets the floor (at most the row) and the trailing items wrap to the next line.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const recalc = () => {
      const rowWidth = row.clientWidth;
      if (!rowWidth) return setBudget(null);
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const others = Array.from(row.children).slice(1);
      const used = others.reduce((total, el) => total + el.getBoundingClientRect().width + gap, 0);
      const left = Math.floor(rowWidth - used);
      const minimum = floor ?? FALLBACK_BADGE_FLOOR;
      setBudget(left >= minimum ? left : Math.min(rowWidth, minimum));
    };
    recalc();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(recalc);
    observer.observe(row);
    Array.from(row.children).slice(1).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [categoryCount, refreshedAt, header.asin_count, header.trailing_12mo_revenue, floor]);

  return (
    <div ref={rowRef} className="flex flex-wrap items-center gap-1.5 mt-1 text-sm text-muted-foreground">
      <div data-testid="meta-badge-item" className="flex min-w-0 max-w-full empty:hidden" style={budget === null ? undefined : { maxWidth: budget }}>
        <CategoryNamesFitBudget.Provider value={context}>{badge}</CategoryNamesFitBudget.Provider>
      </div>
      <span className="inline-flex items-center tabular-nums">
        {categoryCount}
        <InfoTooltip
          text={
            categoryCountScope === "other"
              ? BI_TOOLTIPS.otherCategoryCount(header.bsr_threshold)
              : BI_TOOLTIPS.categoryCount(header.bsr_threshold)
          }
        />
      </span>
      <span>·</span>
      <span className="inline-flex items-center tabular-nums">
        {plural(header.asin_count, "ASIN", "ASINs")}
        <InfoTooltip text={BI_TOOLTIPS.asinCount} />
      </span>
      <span>·</span>
      <span className="inline-flex items-center">
        <span className="font-semibold text-foreground tabular-nums">{fmtUsd(header.trailing_12mo_revenue)}</span>
        <span className="ml-1">L12 revenue</span>
        <InfoTooltip text={BI_TOOLTIPS.trailingRevenue} />
      </span>
      {refreshedAt && (
        <>
          <span>·</span>
          <span className="inline-flex items-center">
            Last refreshed {refreshedAt}
            <InfoTooltip text={BI_TOOLTIPS.lastRefreshed} />
          </span>
        </>
      )}
    </div>
  );
}
